import { Controller } from '@hotwired/stimulus'
import { delay } from 'util/delay'

// Connects to data-controller="form"
export default class extends Controller {
  static targets = ['primaryField', 'submit']

  static values = {
    confirmation: String,
    valid: {
      type: Boolean,
      default: true
    }
  }

  async connect () {
    this.triggeredDidAppear = false

    // Create an intersection observer to detect when the form becomes visible
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.didAppear()
        }
      })
    })

    this.observer.observe(this.element)

    this.element.addEventListener('turbo:submit-start', this.onSubmitStart)
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd)
    this.element.addEventListener('direct-uploads:start', this.onSubmitStart)

    await this.#focusOnField()

    this.element.querySelectorAll('.field_with_errors').forEach(div => {
      // if error div only has a .sr-only within it, then add sr-only here too
      if (div.querySelector('.sr-only') && div.querySelectorAll('*').length === 1) {
        div.classList.add('sr-only')
      }
    })
  }

  didAppear () {
    if (this.triggeredDidAppear) {
      return
    }

    this.triggeredDidAppear = true

    this.dispatch('did-appear')
  }

  disconnect () {
    this.element.removeEventListener('turbo:submit-start', this.onSubmitStart)
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd)

    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  get form () {
    if (this.element.nodeName === 'FORM') {
      return this.element
    } else {
      return this.element.closest('form')
    }
  }

  get submitButton () {
    if (this.hasSubmitTarget) {
      return this.submitTarget
    }

    const { form } = this

    if (!form) {
      return null
    }

    let submit = form.querySelector('input[type=submit]')

    if (submit) {
      return submit
    }

    submit = form.querySelector('button[type=submit]')

    if (submit) {
      return submit
    }

    return null
  }

  get url () {
    return this.element.getAttribute('action')
  }

  set url (value) {
    this.element.setAttribute('action', value)
  }

  focus () {
    console.log('focus')

    if (this.hasPrimaryFieldTarget) {
      this.#focusOnField()
    }
  }

  markAsValid () {
    this.validValue = true
  }

  markAsInvalid () {
    this.validValue = false
  }

  onSubmitEnd = event => {
    this.enableButton()

    if (event.detail.success !== true) {
      return
    }

    if (this.hasModalOutlet) {
      this.modalOutlet.hide()
    }
  }

  onSubmitStart = event => {
    this.disableButton()

    this.dispatch('submitted')
  }

  validValueChanged (formIsValid) {
    if (formIsValid === true) {
      this.enableButton()
    } else {
      this.disableButton()
    }
  }

  confirm (event) {
    if (!this.hasConfirmationValue) {
      return
    }

    if (!window.confirm(this.confirmationValue)) {
      event.preventDefault()
    }
  }

  disableButton () {
    const { submitButton } = this

    if (submitButton) {
      submitButton.setAttribute('disabled', 'disabled')
    }
  }

  enableButton () {
    const { submitButton } = this

    if (submitButton) {
      submitButton.removeAttribute('disabled')
    }
  }

  submit (event = null) {
    if (event) {
      event.preventDefault()
    }

    this.form.submit()
  }

  async #focusOnField () {
    if (!this.hasPrimaryFieldTarget) {
      return
    }

    // give modals and forms a chance to open
    await delay(100)

    const focusMethod = this.primaryFieldTarget.getAttribute('data-focus-method')

    if (focusMethod === 'select') {
      this.primaryFieldTarget.select()
    } else {
      this.primaryFieldTarget.focus()
    }
  }
}
